import { AnimatedReveal } from "@/components/hero/shared/AnimatedReveal";
import { Button } from "@/components/ui/button";
import { ArrowUpRight, ChefHat, Users, MessageCircle } from "lucide-react";

// Sister-product callout placed between the white Pricing section and the
// butter Final CTA. The cream ground is a deliberate tonal step that warms
// the page back toward butter without competing with either neighbour.
//
// Voice and structure mirror the Belief Belt above: italic Newsreader
// headline, coral eyebrow, considerate spacing. Three small cards keep this
// from reading as a banner, they give the section enough body to justify
// the pause without pretending it's the main pitch.
//
// The CTA links to the static /catering landing page (served from
// publichttps://catering.lembra.com.au via the _redirects rewrite). Plain <a> on purpose:
// React Router has no /catering route, so client-side navigation would
// 404; a real anchor lets the host serve the static file directly.

const cards = [
  {
    icon: ChefHat,
    title: "Multi-vendor, one approval",
    desc: "Custom cupcakes from one bakery, hot food from another, coffee from down the road. Lembra consolidates the lot into one brief, one quote, one delivery.",
  },
  {
    icon: Users,
    title: "Your branding, on the food",
    desc: "Upload your logos and formatting once. Vendors receive your brand materials with the order, so packaging and signage stay on-brand without you chasing it.",
  },
  {
    icon: MessageCircle,
    title: "Local vendors, wherever the event is",
    desc: "City office, regional warehouse, a conference two hours outside the CBD, Lembra sources near the venue, not the other way around.",
  },
];

export function CateringTeaser() {
  return (
    <section
      aria-label="Lembra Catering, sister product"
      className="relative overflow-hidden pt-12 lg:pt-16 pb-20 lg:pb-28"
      style={{ position: "relative", zIndex: 3, background: "#FAF6F0" }}
    >
      {/* Ambient blobs, same motif as the Final CTA below, dialled down so
          this beat reads as quieter than the closing pitch. */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -top-16 -right-16 h-72 w-72 rounded-full"
        style={{ background: "radial-gradient(circle, rgba(196,86,74,0.10) 0%, transparent 70%)", filter: "blur(60px)" }}
      />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -bottom-24 -left-20 h-80 w-80 rounded-full"
        style={{ background: "radial-gradient(circle, rgba(168,197,226,0.18) 0%, transparent 70%)", filter: "blur(70px)" }}
      />

      <div className="relative px-6" style={{ maxWidth: 1100, margin: "0 auto" }}>
        <div className="text-center mx-auto" style={{ maxWidth: "44rem" }}>
          <AnimatedReveal animation="fadeInUp" duration={700}>
            <p
              className="mb-3"
              style={{
                fontFamily: "'JetBrains Mono', ui-monospace, SFMono-Regular, Menlo, monospace",
                fontSize: 11,
                fontWeight: 500,
                letterSpacing: "0.18em",
                textTransform: "uppercase",
                color: "#C4564A",
              }}
            >
              Also from Lembra
            </p>
            <h2
              style={{
                fontFamily: "Newsreader, Georgia, serif",
                fontStyle: "italic",
                fontWeight: 600,
                fontSize: "clamp(2rem, 4vw, 3rem)",
                lineHeight: 1.12,
                letterSpacing: "-0.01em",
                color: "#1D3461",
                margin: 0,
              }}
            >
              Catering, the way you'd brief a colleague.
            </h2>
            <p
              className="mt-5"
              style={{
                fontFamily: "DM Sans, ui-sans-serif, system-ui, sans-serif",
                fontSize: "clamp(1rem, 1.4vw, 1.0625rem)",
                lineHeight: 1.6,
                color: "rgba(45,41,38,0.72)",
                maxWidth: "42rem",
                margin: "20px auto 0",
              }}
            >
              Brief Lembra in plain language, menu, headcount, dietaries, branding. We dispatch
              the inquiries, collect the quotes, and coordinate the delivery, so organising the
              food stops eating your week.
            </p>
          </AnimatedReveal>
        </div>

        {/* Three small feature cards, lighter weight than the main Features
            grid above; this is a "by the way" beat, not a second pitch. */}
        <div className="mt-12 grid gap-5 md:grid-cols-3">
          {cards.map((c, i) => {
            const Icon = c.icon;
            return (
              <AnimatedReveal key={c.title} animation="fadeInUp" duration={550} delay={120 + i * 90} className="h-full">
                <div
                  className="group h-full rounded-2xl p-6 bg-white/85 hover:bg-white hover:-translate-y-0.5 transition-all duration-300"
                  style={{
                    border: "1px solid rgba(29,52,97,0.06)",
                    boxShadow: "0 1px 0 rgba(29,52,97,0.04), 0 8px 24px -16px rgba(29,52,97,0.12)",
                  }}
                >
                  <div
                    className="mb-4 flex h-10 w-10 items-center justify-center rounded-xl transition-colors duration-300 group-hover:bg-coral/15"
                    style={{ background: "rgba(196,86,74,0.08)", border: "1px solid rgba(196,86,74,0.14)" }}
                  >
                    <Icon size={18} className="text-coral" />
                  </div>
                  <h3 className="mb-1.5 text-base font-semibold text-navy tracking-tight">{c.title}</h3>
                  <p className="text-sm text-charcoal/72 leading-relaxed">{c.desc}</p>
                </div>
              </AnimatedReveal>
            );
          })}
        </div>

        {/* CTA + honesty line. The arrow-up-right is the standard "this leaves
            the celebrations app" affordance; the small note signals catering's
            current early-access state without making the CTA feel tentative. */}
        <AnimatedReveal animation="fadeInUp" duration={600} delay={420}>
          <div className="mt-14 text-center">
            <Button
              asChild
              variant="primary"
              size="lg"
              className="rounded-full px-7 shadow-lg hover:shadow-xl transition-shadow"
            >
              <a href="https://catering.lembra.com.au">
                Explore Lembra Catering
                <ArrowUpRight size={16} className="ml-2" aria-hidden="true" />
              </a>
            </Button>
            <p className="mt-4 text-xs text-charcoal/55">
              Currently in early access, a separate product with its own signup.
            </p>
          </div>
        </AnimatedReveal>
      </div>
    </section>
  );
}
